import React, {useEffect, useState} from "react"

import {TextField, Typography} from "@mui/material"
import Grid from "@mui/material/Grid2"
import {useDispatch, useSelector} from "react-redux"
import {setLength, setWallWidth, setWidth} from "../../../features/plan/planRootSlice"

import {VaultPlan} from "../../Plan/Configuration/VaultPlan/VaultPlan"
import {
    planLimits,
    getVaultHeightError,
    getWallWidthError,
    isVaultHeightValid,
    isWallWidthValid,
    getLengthError,
    isLengthValid,
    getWidthError,
    isWidthValid,
} from "../../../validation/form/planFormValidation"
import {removeManhole} from "../../../features/lid/lidSlice"
import {setVaultHeight} from "../../../features/vault/vaultSlice"
import {applyStandardVault} from "../../../features/vault/applyStandardVaultThunk"
import InfoIcon from "@mui/icons-material/Info"
import {ImageButton} from "./ImageButton"
import {GroundPlan} from "../../Plan/Configuration/GroundPlan/GroundPlan"
import {lidIsFullyCoveredByOrHasNoManholesAtAll} from "../../domain/lid";
import {getVaultPresetDimensions, getStockVaultDimensions} from "../../domain/vault";
import {DimensionsIcon} from "./DimensionsIcons";

const TitleGridItem = () => {
    return (<Grid size={12}>
        <Typography component="h1" variant="h4">
            Abmessung
        </Typography>
        < hr/>
    </Grid>)
}

const StandardDimensionsGridItem = props => {
    const {setFocusLengthField} = props
    const dispatch = useDispatch()
    const plan = useSelector(state => state.plan)
    const lid = useSelector(state => state.plan.lid)
    const stockVault = useSelector(state => !!state.plan.vault && state.plan.vault.isStockVault ?
        state.plan.vault :
        null)
    const stockFloor = useSelector(state => !!state.plan.floor && state.plan.floor.isStockFloor ?
        state.plan.floor :
        null)
    const stockDimensions = getStockVaultDimensions(plan)
    const standardDimensions = getVaultPresetDimensions(plan)
    const isStockVaultDimension = d => stockDimensions.some(sd => sd.length === d.length && sd.width === d.width)
    const isStandardVaultDimension = d => standardDimensions.some(sd => sd.length === d.length && sd.width === d.width)
    const [userClickedCustomVault, setUserClickedCustomVault] = useState(false);
    const unsetStandardVault = () => {
        if (!!lid &&
            isStandardVaultDimension({length: plan.length, width: plan.width}) &&
            lid.manholes.length >
            0 &&
            lidIsFullyCoveredByOrHasNoManholesAtAll(plan)) {
            dispatch(removeManhole({id: lid.manholes[0].id}))
        }
        setUserClickedCustomVault(true);
    }

    return (<Grid size={12}>
        <Typography component="p" variant="body1" style={{marginBottom: '0.5em'}}>
            {!!stockVault || !!stockFloor ?
                "Wählen Sie eine Standard-Grösse aus:" :
                "Wählen Sie eine Standard-Grösse aus oder definieren Sie die Abmessung selbst (in 5 cm Schritten):"}
        </Typography>
        <Grid container direction={'row'} justifyContent={'flex-start'} alignItems={'flex-end'} spacing={3}>
            {getVaultPresetDimensions(plan)
                .map(d => <Grid key={d.length + ' x ' + d.width + ' cm'}>
                    <ImageButton
                        disabled={(!!stockVault || !!stockFloor) && !isStockVaultDimension(d)}
                        title={d.length + ' x ' + d.width + ' cm'}
                        checked={!userClickedCustomVault && d.length === plan.length && d.width === plan.width}
                        onClick={() => {
                            setFocusLengthField(false)
                            dispatch(applyStandardVault(d, lid, stockVault, stockFloor))
                            setUserClickedCustomVault(false);
                        }}
                    >
                        <DimensionsIcon width={d.length} height={d.width}/>
                    </ImageButton>
                </Grid>)}
            <Grid>
                <ImageButton
                    disabled={!!stockVault || !!stockFloor}
                    title={'Benutzerdefiniert'}
                    checked={!isStandardVaultDimension({length: plan.length, width: plan.width}) ||
                        userClickedCustomVault}
                    onClick={() => {
                        unsetStandardVault()
                        setFocusLengthField(true)
                    }}
                >
                    <DimensionsIcon width={200} height={140}/>
                </ImageButton>
            </Grid>
        </Grid>
    </Grid>)
}

const StandardDimensionsRemarksTextGridItem = () => {
    return (<Grid size={12} key={'upper_text'}>
        <Grid direction={"row"} container alignItems={'center'}>
            <Grid >
                <InfoIcon style={{marginRight: '1em'}}/>
            </Grid>
            <Grid size={10}>
                <Typography component="p" variant="body1">
                    Die Standardabmessungen sind von der Deckelklasse abhänging!
                </Typography>
            </Grid>
        </Grid>
    </Grid>)
}

const LengthField = props => {
    const {focusLengthField, setFocusLengthField} = props
    const dispatch = useDispatch()
    const stockVault = useSelector(state => !!state.plan.vault && state.plan.vault.isStockVault ?
        state.plan.vault :
        null)
    const stockFloor = useSelector(state => !!state.plan.floor && state.plan.floor.isStockFloor ?
        state.plan.floor :
        null)
    const length = useSelector(state => state.plan.length)
    const [currentLength, setCurrentLength] = useState(length)
    const error = getLengthError(currentLength)

    useEffect(() => {
        setCurrentLength(length)
    }, [length])
    return (<TextField
        disabled={!!stockVault || !!stockFloor}
        inputRef={input => {
            if (input != null && focusLengthField) {
                input.focus()
            }
        }}
        onBlur={() => setFocusLengthField(false)}
        onFocus={event => event.target.select()}
        type="number"
        slotProps={{
            htmlInput: {
                min: planLimits.minLength,
                max: planLimits.maxLength,
                step: planLimits.lengthStepWidth
            }
        }}
        variant="outlined"
        fullWidth
        margin="normal"
        id="length"
        label="Länge im Licht [cm]"
        name="length"
        value={currentLength}
        error={!!error}
        helperText={error}
        onChange={e => {
            setCurrentLength(e.target.value)
            if (isLengthValid(e.target.value)) {
                dispatch(setLength(parseInt(e.target.value)))
            }
        }}
    />)
}

const WidthField = () => {
    const dispatch = useDispatch()
    const stockVault = useSelector(state => !!state.plan.vault && state.plan.vault.isStockVault ?
        state.plan.vault :
        null)
    const stockFloor = useSelector(state => !!state.plan.floor && state.plan.floor.isStockFloor ?
        state.plan.floor :
        null)
    const width = useSelector(state => state.plan.width)
    const [currentWidth, setCurrentWidth] = useState(width)
    const error = getWidthError(currentWidth)

    useEffect(() => {
        setCurrentWidth(width)
    }, [width])
    return (<TextField
        disabled={!!stockVault || !!stockFloor}
        onFocus={event => event.target.select()}
        type="number"
        slotProps={{
            htmlInput: {
                min: planLimits.minWidth,
                max: planLimits.maxWidth,
                step: planLimits.widthStepWidth
            }
        }}
        variant="outlined"
        fullWidth
        margin="normal"
        id="length"
        label="Breite im Licht [cm]"
        name="width"
        value={currentWidth}
        error={!!error}
        helperText={error}
        onChange={e => {
            setCurrentWidth(e.target.value)
            if (isWidthValid(e.target.value)) {
                dispatch(setWidth(parseInt(e.target.value)))
            }
        }}
    />)
}

const GroundPlanInputFieldsGridItem = props => {
    const {focusLengthField, setFocusLengthField} = props

    return (<Grid size={12}>
        <Grid container spacing={1}>
            <Grid size={6}>
                <LengthField focusLengthField={focusLengthField} setFocusLengthField={setFocusLengthField}/>
            </Grid>
            <Grid size={6}>
                <WidthField/>
            </Grid>
        </Grid>
    </Grid>)
}

const GroundPlanGirdItem = () => {
    return (<Grid size={12}>
        <GroundPlan/>
    </Grid>)
}

const HeightField = () => {
    const dispatch = useDispatch()
    const vault = useSelector(state => state.plan.vault)
    const customVault = !!vault && !vault.isStockVault ? vault : null;
    const height = vault ? vault.height : 0
    const [currentHeight, setCurrentHeight] = useState(height)
    const error = !vault ? null : getVaultHeightError(currentHeight)

    useEffect(() => {
        setCurrentHeight(height)
    }, [height])
    return (<TextField
        disabled={!customVault}
        onFocus={event => event.target.select()}
        type="number"
        slotProps={{
            htmlInput: {
                min: planLimits.minVaultHeight,
                max: planLimits.maxVaultHeight,
                step: planLimits.vaultStepWidth
            }
        }}
        variant="outlined"
        fullWidth
        margin="normal"
        id="height"
        label="Höhe [cm]"
        name="height"
        value={currentHeight}
        error={!!error}
        helperText={error}
        onChange={e => {
            setCurrentHeight(e.target.value)
            if (isVaultHeightValid(e.target.value)) {
                dispatch(setVaultHeight({height: parseInt(e.target.value)}))
            }
        }}
    />)
}

const WallWidthField = () => {
    const dispatch = useDispatch()
    const stockVault = useSelector(state => !!state.plan.vault && state.plan.vault.isStockVault ?
        state.plan.vault :
        null)
    const stockFloor = useSelector(state => !!state.plan.floor && state.plan.floor.isStockFloor ?
        state.plan.floor :
        null)
    const wallWidth = useSelector(state => state.plan.wallWidth)
    const [currentWallWidth, setCurrentWallWidth] = useState(wallWidth)
    const error = getWallWidthError(currentWallWidth)

    useEffect(() => {
        setCurrentWallWidth(wallWidth)
    }, [wallWidth])
    return (<TextField
        disabled={!!stockVault || !!stockFloor}
        onFocus={event => event.target.select()}
        type="number"
        slotProps={{
            htmlInput: {
                min: planLimits.minWallWidth,
                max: planLimits.maxWallWidth,
                step: planLimits.wallWidthStepWidth
            }
        }}
        variant="outlined"
        fullWidth
        margin="normal"
        id="wallWidth"
        label="Wandstärke [cm]"
        name="wallWidth"
        value={currentWallWidth}
        error={!!error}
        helperText={error}
        onChange={e => {
            setCurrentWallWidth(e.target.value)
            if (isWallWidthValid(e.target.value)) {
                dispatch(setWallWidth(parseInt(e.target.value)))
            }
        }}
    />)
}

const ElevationPlanInputFieldsGridItem = () => {
    return (<Grid size={12}>
        <Grid container spacing={1}>
            <Grid size={6}>
                <HeightField/>
            </Grid>
            <Grid size={6}>
                <WallWidthField/>
            </Grid>
        </Grid>
    </Grid>)
}

const ElevationPlanGridItem = () => {
    return (<Grid size={12}>
        <VaultPlan isToolbarVisible={false}/>
    </Grid>)
}

export const DimensionsSection = () => {
    const lid = useSelector(state => state.plan.lid)
    const [focusLengthField, setFocusLengthField] = useState(false);

    return (<Grid container spacing={4}>
        <TitleGridItem/>
        <StandardDimensionsGridItem setFocusLengthField={setFocusLengthField}/>
        {!!lid && <StandardDimensionsRemarksTextGridItem/>}
        <Grid size={6}>
            <Grid container spacing={3}>
                <GroundPlanInputFieldsGridItem focusLengthField={focusLengthField}
                                               setFocusLengthField={setFocusLengthField}/>
                <GroundPlanGirdItem/>
            </Grid>
        </Grid>
        <Grid size={6}>
            <Grid container spacing={3}>
                <ElevationPlanInputFieldsGridItem/>
                <ElevationPlanGridItem/>
            </Grid>
        </Grid>
    </Grid>)
}
